import { ShieldAlert } from 'lucide-react';
import { EmptyState, Meta } from '@/components/common';
import { NotificationsCard } from '@/components/features/security/NotificationsCard';
import { RevokeAllSessionsCard } from '@/components/features/security/RevokeAllSessionsCard';
import { SecurityPolicyCard } from '@/components/features/security/SecurityPolicyCard';
import { Container } from '@/components/layout';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useSystemSettings } from '@/services/queries';
import { useAppSelector } from '@/store/hooks';

/**
 * The staff-wide settings screen the design draws: security policy,
 * notification digests, and the bulk session kill switch. Superadmin only,
 * since these are org-wide policy for every NuDent staff account, not a
 * personal setting. Second-factor enrolment happens at login (`MfaForm`'s
 * setup step) while Require MFA is on, so there is no separate card for it.
 */
export function SecurityPage() {
  const role = useAppSelector((state) => state.auth.user?.role);
  const isSuperadmin = role === 'superadmin';
  const settings = useSystemSettings();

  return (
    <>
      <Meta title="Settings" />

      <Container className="space-y-6">
        {/*
          Said outright rather than leaving an empty page: a support admin who
          opens Settings should learn why there is nothing here, not wonder
          whether it failed to load. The server refuses these endpoints to
          anyone else regardless - this is the explanation, not the guard.
        */}
        {role && !isSuperadmin && (
          <Card>
            <EmptyState
              icon={ShieldAlert}
              title="Only superadmins can see these settings"
              description="Security policy, notification digests and signing everyone out apply to every NuDent staff account, so only a superadmin can view or change them. Ask a superadmin if something here needs changing."
            />
          </Card>
        )}

        {isSuperadmin && (
          <>
            {settings.isPending && <Skeleton className="h-48 w-full" />}
            {settings.data && (
              <>
                <SecurityPolicyCard settings={settings.data} />
                <NotificationsCard settings={settings.data} />
                <RevokeAllSessionsCard />
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
}
